'use client';

import React, { useState, useEffect } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { Knowledge } from '../hooks/useKnowledge';
import styles from './EntityGenerationSidebar.module.css';

interface EditKnowledgeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  knowledge: Knowledge | null;
  onSave: (knowledgeId: string, data: { knowledgeName: string; knowledgeDescription: string }) => Promise<void>;
  onSuccess?: () => void;
}

export default function EditKnowledgeSidebar({
  isOpen,
  onClose,
  knowledge,
  onSave,
  onSuccess,
}: EditKnowledgeSidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && knowledge) {
      setTitle(knowledge.knowledgeName || '');
      setDescription(knowledge.knowledgeDescription || '');
    }
  }, [isOpen, knowledge]);

  const handleSave = async () => {
    if (!knowledge) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      showToast(t('knowledge_edit_title_required') || 'Title is required', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await onSave(knowledge.id, {
        knowledgeName: trimmedTitle,
        knowledgeDescription: description.trim(),
      });
      showToast(t('knowledge_edit_success') || 'Collection updated successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating knowledge:', error);
      showToast(t('knowledge_edit_error') || 'Failed to update collection', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!knowledge) return null;

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        <div className={styles.header}>
          <img src={isDarkMode ? '/assets/identity/logo-dark.svg' : '/assets/identity/logo.svg'} alt="Thescon" width={16} height={16} />
          <span>|</span>
          <h2>{t('knowledge_edit_title') || 'Edit Collection'}</h2>
        </div>

        <div className={styles.section}>
          <GlobalInput
            label={t('knowledge_edit_name_label') || 'Title'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isLoading}
            placeholder={t('knowledge_edit_name_placeholder') || 'Collection title'}
          />
          <GlobalTextarea
            label={t('knowledge_edit_description_label') || 'Description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
            placeholder={t('knowledge_edit_description_placeholder') || 'Optional description'}
          />
        </div>

        <div className={styles.actionSection}>
          <GlobalButton onClick={handleSave} disabled={isLoading} isLoading={isLoading}>
            {t('project_page_save') || 'Save'}
          </GlobalButton>
        </div>
      </div>
    </GlobalSidebar>
  );
}
